import React, { ChangeEvent, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Input, Slider, Tooltip } from 'antd';
import Select from 'src/components/common/Select';
import { SelectValue } from 'antd/lib/select';
import clsx from 'clsx';
import { isArray } from 'src/utils/typeGuards';
import { objectKeys } from 'src/utils/objectKeys';
import routes from 'src/routes';
// TODO: UI-2758 temporary removing categories
// import ProductCategory from 'src/components/common/ProductCategory';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import { WML } from '../types/AlertResults';
import { AlertResultsFiltersState } from '../hooks/useAlertResultsFiltersFetch';
import { AlertResultsContext } from '../AlertResultsProvider';
import { FiltersButton } from '../AlertResultsFilters/FiltersButton';
import { getInsertionDateId } from '../services/alertResults.service';
import styles from './Header.module.less';

const { Search } = Input;
const { Option } = Select;

type HeaderFilterKeys = 'alerts' | 'sourceSites' | 'insertionDates';
type HeaderFilters = Pick<AlertResultsFiltersState, HeaderFilterKeys>;
type HeaderProps = {
  alertId?: string;
  alertDate?: string;
  filters: HeaderFilters;
};

export const Header: React.FC<HeaderProps> = ({ alertId, alertDate, filters }) => {
  const { insertionDates, alerts, sourceSites } = filters;
  const { headerFilters, changeHeaderFilters } = useContext(AlertResultsContext);
  const foundedAlertId = alerts.find((item) => item.alertId === alertId) ? alertId : undefined;
  const isDisabled = !headerFilters.alertId || !headerFilters.sourceSiteId || !headerFilters.insertionDate;
  const { uniqueId } = useQueryUrlParams();
  const history = useHistory();
  const handleAlertChange = (alertId?: string) => {
    changeHeaderFilters(alertId ?? null, 'alertId');
    return history.push(`${routes.alertResults}/${alertId ?? ''}`);
  };

  const handleAlertDateChange = (date?: string) => {
    if (alertId) {
      changeHeaderFilters(date ?? null, 'insertionDate');
      return history.push(`${routes.alertResults}/${alertId}/${getInsertionDateId(date)}`);
    }
  };

  const handleUniqueIdentifierChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    changeHeaderFilters(value, 'uniqueIdentifier');
  };

  useEffect(() => {
    if (uniqueId) {
      changeHeaderFilters(uniqueId, 'uniqueIdentifier')
    }
  }, [uniqueId]);

  useEffect(() => {
    changeHeaderFilters(alertId ?? null, 'alertId');
  }, [alertId]);

  useEffect(() => {
    if (alertDate) {
      changeHeaderFilters(alertDate, 'insertionDate');
    }
  }, []);

  return (
    <div className={styles.header}>
      <div className={styles.headerRow}>
        <div className={styles.field}>
          <Select
            value={foundedAlertId}
            placeholder="Alert Name"
            size="large"
            onChange={(val: SelectValue) => {
              // TODO: We need to fix typings
              handleAlertChange((val as string) ?? undefined);
            }}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {alerts?.map((item) => (
              <Option value={item.alertId} key={item.alertId} title={item.alertName}>
                {item.alertName}
              </Option>
            ))}
          </Select>
        </div>
        <div className={clsx(styles.field, !headerFilters.alertId && styles.disabled)}>
          <Select
            placeholder="Site"
            size="large"
            onChange={(val: SelectValue) => {
              // TODO: We need to fix typings
              changeHeaderFilters((val as string) ?? undefined, 'sourceSiteId');
            }}
            disabled={!headerFilters.alertId}
            value={headerFilters.sourceSiteId ?? undefined}
          >
            <Option value="-1">All</Option>
            {sourceSites?.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <Search
          className={clsx(styles.header__search, isDisabled && styles.disabled)}
          placeholder="Search by UID"
          allowClear
          onChange={handleUniqueIdentifierChange}
          disabled={isDisabled}
          value={headerFilters.uniqueIdentifier ?? undefined}
        />
        <FiltersButton />
      </div>
      <div className={styles.headerRow}>
        <div className={clsx(styles.field, !headerFilters.alertId && styles.disabled)}>
          <Select
            placeholder="Alert Dates"
            size="large"
            onChange={(val: SelectValue) => {
              // TODO: We need to fix typings
              handleAlertDateChange((val as string) ?? undefined);
            }}
            disabled={!headerFilters.alertId}
            value={headerFilters.insertionDate ?? undefined}
          >
            {insertionDates?.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
        {/* // TODO: UI-2758 temporary removing categories  */}
        {/* <div className={clsx(styles.field, styles.categories, !headerFilters.sourceSiteId && styles.disabled)}>
          <ProductCategory
            className="ant-select-selection"
            sourceOwnerId={headerFilters.sourceSiteId ?? '-1'}
            onChange={(val: string[]) => {
              changeHeaderFilters(val.at(-1) ?? null, 'sourceCategoryId');
            }}
          />
        </div> */}
        <div className={clsx(styles.field, isDisabled && styles.disabled)}>
          <Select
            placeholder="Priced Below / Similar Price / Priced Above"
            size="large"
            onChange={(val: SelectValue) => {
              // TODO: We need to fix typings
              changeHeaderFilters((val as keyof typeof WML) ?? undefined, 'wml');
            }}
            allowClear
            disabled={isDisabled}
          >
            {objectKeys(WML).map((item) => (
              <Option value={item} key={WML[item]}>
                {WML[item]}
              </Option>
            ))}
          </Select>
        </div>
        <div className={clsx(styles.field, styles.slider, isDisabled && styles.disabled)}>
          <h5>
            <Tooltip title="Dropdown selection indicates what variance the slider represents" placement="top">
              Variance
            </Tooltip>
          </h5>

          <Slider
            range
            min={-100}
            max={100}
            defaultValue={[-100, 100]}
            tipFormatter={(value) => {
              switch (value) {
                case -100:
                  return `${value} and below`;
                case 100:
                  return `${value} and above`;
              }
              return value;
            }}
            step={1}
            onAfterChange={(val) => changeHeaderFilters(isArray(val) ? val : null, 'variance')}
            disabled={isDisabled}
          />
        </div>
      </div>
    </div>
  );
};
